/**
 * Botón accesible: solo tokens del sistema, tamaño táctil mínimo, foco visible.
 * No conoce dominio. Copiado de Sentinel (ADR-0002); jerarquía ChileSST cs-20261017.
 */
import type { ReactNode } from 'react'

export interface BotonProps {
  children: ReactNode
  tipo?: 'button' | 'submit'
  /**
   * `primario`: la acción que cierra o hace avanzar la vista (una por vista).
   * `afirmativo`: visto bueno de un elemento de una lista; no compite con la primaria.
   * `secundario`: volver, editar, alternativas. `peligro`: descarta o elimina, nunca rellena.
   * `sutil`: acción menor con aspecto de enlace.
   */
  variante?: 'primario' | 'afirmativo' | 'secundario' | 'peligro' | 'sutil'
  deshabilitado?: boolean
  /** Actividad real de la operación; nunca se deduce del texto. */
  trabajando?: boolean
  alPulsar?: () => void
  etiquetaAccesible?: string
  /** `aria-describedby` nativo: p. ej. el motivo por el que el botón está deshabilitado. */
  describidoPor?: string
  compacto?: boolean
}

const CLASES: Record<NonNullable<BotonProps['variante']>, string> = {
  primario: 'sst-boton-primario text-white',
  afirmativo: 'sst-boton-afirmativo',
  secundario: 'sst-boton-secundario',
  peligro: 'sst-boton-riesgo',
  sutil: 'sst-boton-sutil',
}

export function Boton(props: BotonProps) {
  const variante = props.variante ?? 'primario'
  const altura = props.compacto ? 'min-h-[40px] px-4 text-pequeno' : 'min-h-tactil px-5 text-cuerpo'
  return (
    <button
      type={props.tipo ?? 'button'}
      disabled={props.trabajando || (props.deshabilitado ?? false)}
      aria-busy={props.trabajando || undefined}
      onClick={props.alPulsar ?? undefined}
      aria-label={props.etiquetaAccesible}
      aria-describedby={props.describidoPor}
      className={`${altura} scroll-mt-16 rounded-pill font-semibold disabled:cursor-not-allowed disabled:opacity-50 ${CLASES[variante]}`}
    >
      <span className="inline-flex items-center justify-center gap-2">
        {props.trabajando ? <span className="aro-de-espera" aria-hidden="true" /> : null}
        {props.children}
      </span>
    </button>
  )
}
